import { Key, useEffect, useRef, useState } from 'react'
import { navigate } from 'gatsby'
import { useMount } from 'ahooks'
import { decode as decodeBase64 } from 'base-64'
import moment from 'moment'
import Layout from 'components/layouts/tow-column'
import clsx from 'clsx'
import { time } from 'lib/str-formatter'
import {
  Alert,
  Button,
  Form,
  message,
  notification,
  SEO,
  Space,
  TitleAndDescription,
  TitleAndDescriptionFormValues,
} from 'components/common'
import {
  RateStatusSelect,
  StatusFormValue,
  UnavailableItemsCard,
  Variant,
  VariantInputs,
} from 'components/rates'
import {
  NotForSaleCreateInput,
  ProductsQuery,
  RateVariantCreateInput,
  useProductsLazyQuery,
  useRateCreateMutation,
} from 'lib/graphql/storeadmin'
import { routes } from 'config'

type FormValues = TitleAndDescriptionFormValues &
  StatusFormValue & {
    variants: Variant[]
  }

type NodeType = {
  key: string
  title: string
  productName: string
}

export type NotForSale = {
  id?: string
  variants?: []
}

const onBack = () => navigate('../')

const DEFAULT_VARIANTS = [
  {
    start: '',
    end: '',
  },
]

const openNotification = (type: 'success' | 'error', message: string) => {
  notification[type]({
    message: message,
  })
}

export const Head = () => <SEO title="料金テーブルの追加" />

const IndexPage = (): JSX.Element => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [nowUpdating, setNowUpdating] = useState(false)
  const [hasUnavailable, setHasUnavailable] = useState(false)
  const [hideWarning, setWarning] = useState(false)
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([])
  const treeNode = useRef<NodeType[]>([])
  const [selectedVariants, setSelectedVariants] = useState<NodeType[]>([])
  const [storeNotForSale, setStoreNotForSale] = useState<NotForSale[]>([])
  const [mutate] = useRateCreateMutation({})
  const [searchFiled, setSearchFiled] = useState<string>(null)
  const [getProducts, { data: productData, loading: productsLoading }] =
    useProductsLazyQuery({
      variables: { name_Contains: searchFiled },
    })
  const [rateErrorDescription, setRateErrorDescription] = useState<
    string | null
  >(null)

  useMount(() => {
    setLoading(false)
    setSelectedVariants(selectedVariants || [])
    setCheckedKeys(checkedKeys || [])
  })

  useEffect(() => {
    form.setFieldsValue({ variants: DEFAULT_VARIANTS })
  }, [form])

  const onCancel = (): void => {
    navigate('../')
  }

  const handleSearch = (e) => {
    setSearchFiled(e)
  }

  const onCheck = (keys: Key[]) => {
    setCheckedKeys(keys)
  }

  const openTree = (data: ProductsQuery) => {
    const treeData = []
    const node = []
    data?.products?.edges?.forEach((product) => {
      const children = []
      product?.product?.variants?.edges.forEach((variant) => {
        const child = {
          key: variant?.variant?.id,
          title: variant?.variant?.title?.jaJp,
          productName: product?.product?.name?.jaJp,
        }
        children.push(child)
        node.push(child)
      })
      treeData.push({
        title: product?.product?.name?.jaJp,
        key: product?.product?.id,
        children: children,
      })
    })
    treeNode.current = node
    return treeData
  }

  const onHold = () => {
    const selectItems = []
    checkedKeys?.forEach((key) => {
      treeNode?.current?.forEach((node) => {
        if (node.key === key) {
          selectItems.push({
            key: key,
            productName: node.productName,
            title: node.title,
          })
        }
      })
    }, selectItems)
    setSelectedVariants(selectItems)
  }

  const onNotForSellDelete = (key) => {
    try {
      if (typeof key === 'string') {
        if (selectedVariants.length === 1) {
          setHasUnavailable(false)
          setWarning(false)
        }
        setSelectedVariants((value) => {
          return value.filter((obj) => obj.key !== key)
        })
      }
    } catch (e) {
      openNotification('error', '予期せぬ問題が発生しました。')
    } finally {
      setWarning(true)
      openNotification('success', '削除されました。')
    }
  }

  const onNotForSaleSaved = () => {
    const selectProductVariantId = []
    const selectProductId = []
    const notForSellVariantNode = []

    // Divided Product id and Product variants id
    if (
      productData?.products?.edges?.length > 0 &&
      selectedVariants?.length > 0
    ) {
      selectedVariants?.forEach((selectedVariant) => {
        productData?.products?.edges?.forEach((product) => {
          product?.product?.variants?.edges?.forEach((variant) => {
            if (variant.variant.id === selectedVariant?.key) {
              selectProductId.push(product.product.id)
              selectProductVariantId.push(variant.variant.id)
            }
          })
        })
      })
    }

    // Filter Duplicate id and return unique Array
    const filteredSelectProductId = selectProductId?.filter(
      (e, i) => selectProductId?.indexOf(e) === i
    )

    // Format data as API Format
    filteredSelectProductId.forEach((newValue, index) => {
      productData?.products?.edges?.forEach((product) => {
        if (product?.product?.id === filteredSelectProductId[index]) {
          // TODO: Remove decodeBase64 after finish API
          const notForSellId = decodeBase64(product?.product?.id).split(':')[1]
          const notForSellVariant = []

          selectProductVariantId.forEach((variantsValue, i) => {
            product?.product?.variants?.edges?.forEach((variant) => {
              if (variant?.variant?.id === selectProductVariantId[i]) {
                notForSellVariant.push(
                  decodeBase64(variant?.variant?.id).split(':')[1]
                )
              }
            })
          })
          notForSellVariantNode.push({
            id: notForSellId,
            variants: notForSellVariant,
          })
        }
      })
    })
    setStoreNotForSale(notForSellVariantNode)
    openNotification('success', '保存しました。')
  }

  const onSubmit = async (values: FormValues) => {
    try {
      setNowUpdating(true)

      if (hasUnavailable && storeNotForSale?.length === 0) {
        setRateErrorDescription('利用不可施設を選択してください。')
        throw new Error('利用不可施設を選択してください。')
      }
      const { title, description, variants, status } = values
      const rateVariants = variants.filter((v) => Object.keys(v).length !== 0)
      const inputVariant = [
        ...(rateVariants || []).map<RateVariantCreateInput>((variant) => ({
          start: time(moment(variant?.start)),
          end: time(moment(variant?.end)),
          rate: Number(variant.rate),
        })),
      ]
      const { data } = await mutate({
        variables: {
          input: {
            title,
            description,
            variants: inputVariant,
            status: status,
            notForSale: [
              ...storeNotForSale.map<NotForSaleCreateInput>((value) => ({
                product: value.id,
                variantIds: value.variants,
              })),
            ],
          },
        },
      })

      const id = data?.rateCreate?.rate?.id
      const rateUserErrors = data.rateCreate.rateUserErrors
      if (typeof id !== 'string' || rateUserErrors.length > 0) {
        message.error('入力項目に問題があります。')

        rateUserErrors.forEach(({ field }) => {
          switch (field[0]) {
            case 'title':
              setRateErrorDescription('タイトルは必須です。')
              break
            case 'variants':
              setRateErrorDescription(
                'バリエーションは必ず一つ以上設定してください。'
              )
              break
            default:
              break
          }
        })
      } else {
        await navigate(`${routes.rates}/${id}`)
        openNotification('success', '保存しました。')
        setRateErrorDescription(null)
      }
    } catch (e) {
      // captureException(e)
      console.log(e)
      openNotification('error', '予期せぬ問題が発生しました。')
    } finally {
      setNowUpdating(false)
    }
  }

  const onUnavailable = (value) => {
    setHasUnavailable(value.target.checked)
  }

  const onFinishFailed = () => {
    message.error('入力項目に問題があります。')
  }

  return (
    <>
      <Form
        component="form"
        form={form}
        preserve={false}
        onFinish={onSubmit}
        onFinishFailed={onFinishFailed}
      >
        <Layout title="料金テーブルを追加する" onBack={onBack}>
          <Layout.Section size="large">
            {rateErrorDescription !== null && (
              <Alert
                message="エラー"
                description={rateErrorDescription}
                type="error"
                showIcon
              />
            )}

            <Layout.Item>
              <TitleAndDescription maxTitleLength={64} loading={loading} />
            </Layout.Item>

            <Layout.Item>
              <VariantInputs loading={loading} />
            </Layout.Item>

            <Layout.Item>
              <UnavailableItemsCard
                loading={loading}
                productsLoading={productsLoading}
                hasUnavailable={hasUnavailable}
                onUnavailable={onUnavailable}
                productData={productData}
                getProducts={getProducts}
                onHold={onHold}
                openTree={openTree}
                onCheck={onCheck}
                selectedVariants={selectedVariants}
                handleSearch={handleSearch}
                onDelete={onNotForSellDelete}
                onSaved={onNotForSaleSaved}
                hideWarning={hideWarning}
                setWarning={setWarning}
              />
            </Layout.Item>
          </Layout.Section>

          <Layout.Section size="small">
            <Layout.Item>
              <RateStatusSelect loading={loading} />
            </Layout.Item>
            {/* TODO: Phase 1 では非表示 */}
            {/*<Layout.Item>*/}
            {/*  <Card shadow loading={loading}>*/}
            {/*    <div>*/}
            {/*      /!* TODO: products と共通なのでコンポーネント化する *!/*/}
            {/*      <Title level={5}>分類</Title>*/}
            {/*      <Input />*/}
            {/*    </div>*/}

            {/*    <Card.Divider />*/}

            {/*    <div>*/}
            {/*      /!* TODO: products と共通なのでコンポーネント化する *!/*/}
            {/*      <Title level={5}>コレクション</Title>*/}
            {/*      <Input />*/}
            {/*    </div>*/}

            {/*    <Card.Divider />*/}

            {/*    <div>*/}
            {/*      /!* TODO: orders, products と共通なのでコンポーネント化する *!/*/}
            {/*      <div*/}
            {/*        className={clsx(*/}
            {/*          'flex',*/}
            {/*          'justify-between',*/}
            {/*          'items-center',*/}
            {/*          'mb-3'*/}
            {/*        )}*/}
            {/*      >*/}
            {/*        <Title*/}
            {/*          level={5}*/}
            {/*          style={{ marginBottom: 0 }}*/}
            {/*          space="none"*/}
            {/*        >*/}
            {/*          タグ*/}
            {/*        </Title>*/}
            {/*        <Button type="link">すべてのタグを表示する</Button>*/}
            {/*      </div>*/}
            {/*      <Input />*/}
            {/*    </div>*/}
            {/*  </Card>*/}
            {/*</Layout.Item>*/}
          </Layout.Section>

          <Layout.Footer
            left={
              <Space>
                <Form.Item>
                  <Button danger onClick={onCancel}>
                    キャンセル
                  </Button>
                </Form.Item>
              </Space>
            }
            right={
              <div className={clsx('flex', 'justify-end')}>
                <Form.Item>
                  <Button
                    disabled={hideWarning && selectedVariants.length > 0}
                    type="primary"
                    htmlType="submit"
                    loading={nowUpdating}
                  >
                    保存する
                  </Button>
                </Form.Item>
              </div>
            }
          />
        </Layout>
      </Form>
    </>
  )
}

export default IndexPage
